import { GlobalWizardState, LOBState } from "stores/wizard/state";
import { observable, action, computed } from "mobx";
import { FlightConfig } from "stores/wizard/config";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { Travelers } from "src/components/shared/TravelersField/typings";
import { TypeaheadSelectionProps } from "components/flexComponents/WizardHotelPWA/typings";
import { locationState } from "src/stores/wizard/state/global/location";
import { dateState } from "src/stores/wizard/state/global/date";
import { FlightLegState } from "stores/wizard/state/flight/MultiLegFlights/typings";
import { setCookie } from "components/utility/cookie";
import { FlightLastSearchData, LastSearchData } from "stores/wizard/LastSearchData";
import { DateState, TravelersState } from "stores/wizard/state/global";
import { formatDate } from "bernie-l10n";

export class FlightWizardState implements LOBState {
  private globalState: GlobalWizardState;

  @computed
  public get canTrack() {
    return this.globalState.canTrack;
  }

  public lastSearchData?: FlightLastSearchData;

  @computed
  public get flightClassCode() {
    return this.globalState.flightClassCode;
  }

  @observable
  public isAddAHotelChecked: boolean = false;

  @computed
  public get location() {
    return this.globalState.location;
  }

  @observable
  public config: FlightConfig;

  @observable
  public subLOBState: string = "";

  @observable
  public listOfAdditionalLegs: FlightLegState[];

  @observable
  public multiCityFlightLegURL: string[];

  @observable
  public isAddACarChecked: boolean = false;

  public localDateFormat: string;

  @action
  public buildMultiCityFlightLegUrl = (legIndex: number) => {
    const legConfig = this.config.multiCityFlight.legs[legIndex];
    const rawFormatForSubmission = legConfig.date.ISOSubmissionSupport
      ? legConfig.date.format
      : this.localDateFormat;

    const from = `from:${this.listOfAdditionalLegs[legIndex].location.origin.value}(${
      this.listOfAdditionalLegs[legIndex].location.origin.metaData.ttla
    }),`;
    const to = `to:${this.listOfAdditionalLegs[legIndex].location.destination.value}(${
      this.listOfAdditionalLegs[legIndex].location.destination.metaData.ttla
    }),`;
    const startDate = formatDate(this.listOfAdditionalLegs[legIndex].date.start, {
      raw: rawFormatForSubmission
    });
    const departure = `departure:${startDate}TANYT`;

    return from + to + departure;
  };

  @action
  public updateLegLocationOrigin(selection: TypeaheadSelection, index: number) {
    const { term, data } = selection as TypeaheadSelectionProps;
    const legToUpdate = this.listOfAdditionalLegs[index];
    legToUpdate.location.origin.value = term;
    legToUpdate.location.origin.metaData.regionId = data && data.regionId;
    legToUpdate.location.origin.metaData.ttla =
      data &&
      data.hierarchyInfo &&
      data.hierarchyInfo.airport &&
      data.hierarchyInfo.airport.airportCode;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  }

  @action
  public updateLegLocationDestination(selection: TypeaheadSelection, index: number) {
    const { term, data } = selection as TypeaheadSelectionProps;
    const legToUpdate = this.listOfAdditionalLegs[index];
    legToUpdate.location.destination.value = term;
    legToUpdate.location.destination.metaData.regionId = data && data.regionId;
    legToUpdate.location.destination.metaData.ttla =
      data &&
      data.hierarchyInfo &&
      data.hierarchyInfo.airport &&
      data.hierarchyInfo.airport.airportCode;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  }

  @action
  public updateLegDate = (start: Date, end: Date, index: number) => {
    const legToUpdate = this.listOfAdditionalLegs[index];
    legToUpdate.date.start = start;
    legToUpdate.date.end = end;
    this.multiCityFlightLegURL[index] = this.buildMultiCityFlightLegUrl(index);
  };

  @observable
  public hotelDate: DateState;

  @action
  public updateSubLOBState(subLOBState: string) {
    this.subLOBState = subLOBState;
  }

  @computed
  public get date() {
    return this.globalState.date;
  }

  @computed
  public get travelers() {
    return this.globalState.travelers;
  }

  @computed
  public get hotelTravelersMetadata() {
    return this.globalState.hotelTravelersMetadata;
  }

  @computed
  public get nonHotelTravelersMetadata() {
    return this.globalState.nonHotelTravelersMetadata;
  }

  @computed
  public get flightTripType() {
    if (this.subLOBState === "") {
      return "ROUND_TRIP";
    } else if (this.subLOBState === "oneway") {
      return "ONE_WAY";
    } else {
      return "";
    }
  }

  @computed
  public get flightPackageType() {
    if (this.isAddAHotelChecked && this.isAddACarChecked) {
      return "fhc";
    } else if (this.isAddAHotelChecked) {
      return "fh";
    } else if (this.isAddACarChecked) {
      return "fc";
    } else {
      return "";
    }
  }

  //validations
  @observable
  public originInvalidKey: string | "";

  @action
  public validateOriginField = () => {
    if (!this.globalState.validateOriginField()) {
      this.originInvalidKey = this.config.location.origin.invalidLocationMessageKey;

      return false;
    }

    this.originInvalidKey = "";

    return true;
  };

  @observable
  public destinationInvalidKey: string | "";

  @action
  public validateDestinationField = () => {
    if (!this.globalState.validateDestinationField()) {
      this.destinationInvalidKey = this.config.location.destination.invalidLocationMessageKey;

      return false;
    }

    this.destinationInvalidKey = "";

    return true;
  };

  @observable
  public hotelTravelersInvalidKey: string = "";

  @observable
  public nonHotelTravelersInvalidKey: string = "";

  @action
  public validateLessThanNTravelers = () => {
    if (
      this.isAddAHotelChecked &&
      !this.globalState.validateLessThanNTravelers(
        this.hotelTravelersMetadata,
        this.config.travelersWithHotel
      )
    ) {
      this.hotelTravelersInvalidKey = this.config.travelersWithHotel.invalidLessThanNTravelersMessageToken;

      return false;
    }
    if (
      !this.globalState.validateLessThanNTravelers(
        this.nonHotelTravelersMetadata,
        this.config.travelers
      )
    ) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidLessThanNTravelersMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateUnattendedInfantInLap = () => {
    if (
      this.isAddAHotelChecked &&
      !this.globalState.validateUnattendedInfantInLap(
        this.hotelTravelersMetadata,
        this.config.travelersWithHotel,
        this.travelers.hotel.infantSeating
      )
    ) {
      this.hotelTravelersInvalidKey = this.config.travelersWithHotel.invalidUnattendedInfantOnLapMessageToken;

      return false;
    }
    if (
      !this.globalState.validateUnattendedInfantInLap(
        this.nonHotelTravelersMetadata,
        this.config.travelers,
        this.travelers.nonHotel.infantSeating
      )
    ) {
      this.nonHotelTravelersInvalidKey = this.config.travelers.invalidUnattendedInfantOnLapMessageToken;

      return false;
    }

    return true;
  };

  @action
  public validateTravelersField = () =>
    this.validateLessThanNTravelers() && this.validateUnattendedInfantInLap();

  @observable
  public dateStartInvalidKey: string = "";

  @observable
  public dateEndInvalidKey: string = "";

  @action
  public validateMaxDateRange = () => {
    switch (this.subLOBState) {
      case "":
      case "roundtrip":
        if (
          !this.globalState.validateMaxDateRange(
            this.date.start,
            this.date.end,
            this.config.date.maxDaysRange
          )
        ) {
          this.dateEndInvalidKey = this.config.date.invalidMaxDatesRangeMessageToken;

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validateEndDateField = () => this.validateMaxDateRange();

  @observable
  public hotelStartDateInvalidKey: string = "";

  @action
  public validateHotelStartDateWithinRange = () => {
    switch (this.subLOBState) {
      case "":
      case "roundtrip":
        if (
          this.isAddAHotelChecked &&
          !this.globalState.validatePartialDateWithinRange(
            this.date.start,
            this.date.end,
            this.hotelDate.start
          )
        ) {
          this.hotelStartDateInvalidKey = this.config.hotelDate.invalidStartDateOutOfRangeMessageToken;

          return false;
        }

        return true;
      case "oneway":
        if (this.isAddAHotelChecked && this.hotelDate.start < this.date.start) {
          this.hotelStartDateInvalidKey = this.config.invalidOnewayHotelStartDateMessageToken;

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validateHotelStartDateField = () => this.validateHotelStartDateWithinRange();

  @observable
  public hotelEndDateInvalidKey: string = "";

  @action
  public validateHotelMaxDateRange = () => {
    switch (this.subLOBState) {
      case "":
      case "roundtrip":
      case "oneway":
        if (
          this.isAddAHotelChecked &&
          !this.globalState.validateMaxDateRange(
            this.hotelDate.start,
            this.hotelDate.end,
            this.config.hotelDate.maxDaysRange
          )
        ) {
          this.hotelEndDateInvalidKey = this.config.hotelDate.invalidMaxDatesRangeMessageToken;

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  @action
  public validateHotelEndDateWithinRange = () => {
    switch (this.subLOBState) {
      case "":
      case "roundtrip":
        if (
          this.isAddAHotelChecked &&
          !this.globalState.validatePartialDateWithinRange(
            this.date.start,
            this.date.end,
            this.hotelDate.end
          )
        ) {
          this.hotelEndDateInvalidKey = this.config.hotelDate.invalidEndDateOutOfRangeMessageToken;

          return false;
        }

        return true;
      default:
        return true;
    }
  };

  public validateHotelEndDateField = () =>
    this.validateHotelEndDateWithinRange() && this.validateHotelMaxDateRange();

  @action
  public resetValidations = () => {
    this.originInvalidKey = "";
    this.destinationInvalidKey = "";
    this.dateStartInvalidKey = "";
    this.dateEndInvalidKey = "";
    this.hotelStartDateInvalidKey = "";
    this.hotelEndDateInvalidKey = "";
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  public validateForm = () => {
    const validations: boolean[] = [
      this.validateOriginField(),
      this.validateDestinationField(),
      this.validateTravelersField(),
      this.validateEndDateField(),
      this.validateHotelStartDateField(),
      this.validateHotelEndDateField()
    ];

    return !validations.includes(false);
  };

  // other actions
  @action
  public overrideConfig(callback: () => void): void {
    callback();
  }

  @action
  public updateDateSelection = (start: Date, end: Date, multiLegIndex?: number) => {
    if (multiLegIndex !== undefined) {
      this.updateLegDate(start, end, multiLegIndex);
    } else {
      this.globalState.updateDateSelection(start, end);
      this.dateStartInvalidKey = "";
      this.dateEndInvalidKey = "";
      this.hotelStartDateInvalidKey = "";
      this.hotelEndDateInvalidKey = "";
    }
  };

  @action
  public updateOriginSelection = (selection: TypeaheadSelection, index?: number) => {
    //in the case of a multi-city flight, update the individual leg
    if (index !== undefined) {
      this.updateLegLocationOrigin(selection, index);
    } else {
      this.globalState.updateOriginSelection(selection);
      this.originInvalidKey = "";
    }
  };

  public updateHotelDateSelection = (start: Date, end: Date) => {
    this.hotelDate.start = start;
    this.hotelDate.end = end;
    this.hotelStartDateInvalidKey = "";
    this.hotelEndDateInvalidKey = "";
  };

  @action
  public updateDestinationSelection = (selection: TypeaheadSelection, index?: number) => {
    if (index !== undefined) {
      this.updateLegLocationDestination(selection, index);
    } else {
      this.globalState.updateDestinationSelection(selection);
      this.destinationInvalidKey = "";
    }
  };

  @action
  public swapLocations() {
    this.globalState.swapOriginAndDestinationLocations();
  }

  @action
  public setFlightClass(flightClassCode: string) {
    this.globalState.setFlightClass(flightClassCode);
  }

  @action
  public addALeg = () => {
    if (this.listOfAdditionalLegs.length < this.config.multiCityFlight.maxNumberOfAdditionalLegs) {
      this.listOfAdditionalLegs.push({ location: locationState(), date: dateState() });
    }
  };

  @action
  public removeALeg = (index: number) => {
    if (index < this.listOfAdditionalLegs.length) {
      this.listOfAdditionalLegs.splice(index, 1);
    }
  };

  @action
  public updateDestinationFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.globalState.updateDestinationFromCookie(value, regionID, countryCode, latLong, ttla);
    this.destinationInvalidKey = "";
  }

  @action
  public updateOriginFromCookie(
    value: string,
    regionID: string,
    countryCode: string,
    latLong: string,
    ttla: string
  ) {
    this.globalState.updateOriginFromCookie(value, regionID, countryCode, latLong, ttla);
    this.originInvalidKey = "";
  }

  public setLastSearchCookie = () => {
    if (this.canTrack) {
      const origin = this.location.origin;
      const originMetaData = this.location.origin.metaData;
      const destination = this.location.destination;
      const detinationMetaData = this.location.destination.metaData;
      const date = this.date;
      const hotelDate = this.hotelDate;
      const listOfAddtionalLegs = this.listOfAdditionalLegs;

      const lastSearchCookieObject: LastSearchData = {
        lob: this.config.lastSearch.lobKey,
        flight: {
          origin_value: origin.value ? encodeURIComponent(origin.value) : "",
          origin_regionID: origin.metaData.regionId
            ? encodeURIComponent(this.location.origin.metaData.regionId)
            : "",
          origin_ttla: originMetaData.ttla ? encodeURIComponent(originMetaData.ttla) : "",
          origin_countryCode: originMetaData.countryCode
            ? encodeURIComponent(originMetaData.countryCode)
            : "",
          origin_latLong: originMetaData.latLong ? encodeURIComponent(originMetaData.latLong) : "",
          destination_value: destination.value ? encodeURIComponent(destination.value) : "",
          destination_regionID: detinationMetaData.regionId
            ? encodeURIComponent(detinationMetaData.regionId)
            : "",
          destination_ttla: detinationMetaData.ttla
            ? encodeURIComponent(detinationMetaData.ttla)
            : "",
          destination_countryCode: detinationMetaData.countryCode
            ? encodeURIComponent(detinationMetaData.countryCode)
            : "",
          destination_latLong: detinationMetaData.latLong
            ? encodeURIComponent(detinationMetaData.latLong)
            : "",
          start_date: date.start ? date.start.toString() : "",
          end_date: date.end ? date.end.toString() : "",
          hotel_start_date: hotelDate.start ? hotelDate.start.toString() : "",
          hotel_end_date: hotelDate.end ? hotelDate.end.toString() : "",
          flight_class_code: this.flightClassCode,
          add_hotel_check: this.isAddAHotelChecked.toString(),
          add_car_check: this.isAddACarChecked.toString(),
          list_of_additional_legs: listOfAddtionalLegs ? JSON.stringify(listOfAddtionalLegs) : "",
          travelers: JSON.stringify(this.travelers),
          subLOBState: this.subLOBState
        }
      };
      setCookie({
        key: this.config.lastSearch.lastSearchKey,
        value: JSON.stringify(lastSearchCookieObject)
      });
    }
  };

  public updateStateFromCookie = () => {
    const lastSearchData = this.lastSearchData;

    if (this.canTrack && lastSearchData) {
      const originValue = decodeURIComponent(lastSearchData.origin_value);
      const destinationValue = decodeURIComponent(lastSearchData.destination_value);

      const startDateFromCookie = new Date(lastSearchData.start_date);
      const endDateFromCookie = new Date(lastSearchData.end_date);
      const hotelStartDateFromCookie = new Date(lastSearchData.hotel_start_date);
      const hotelEndDateFromCookie = new Date(lastSearchData.hotel_end_date);

      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      const startDateToUse = startDateFromCookie >= today ? startDateFromCookie : today;
      const endDateToUse = endDateFromCookie >= tomorrow ? endDateFromCookie : tomorrow;
      const hotelStartDateToUse =
        hotelStartDateFromCookie >= today ? hotelStartDateFromCookie : today;
      const hotelEndDateToUse =
        hotelEndDateFromCookie >= tomorrow ? hotelEndDateFromCookie : tomorrow;

      const travelers: TravelersState = lastSearchData.travelers
        ? JSON.parse(lastSearchData.travelers)
        : this.travelers;

      this.subLOBState = lastSearchData.subLOBState ? lastSearchData.subLOBState : this.subLOBState;

      this.updateOriginFromCookie(
        originValue !== "undefined" ? originValue : "",
        lastSearchData.origin_regionID ? lastSearchData.origin_regionID : "",
        lastSearchData.origin_countryCode ? lastSearchData.origin_countryCode : "",
        lastSearchData.origin_latLong ? lastSearchData.origin_latLong : "",
        lastSearchData.origin_ttla ? lastSearchData.origin_ttla : ""
      );

      this.updateDestinationFromCookie(
        destinationValue !== "undefined" ? destinationValue : "",
        lastSearchData.destination_regionID ? lastSearchData.destination_regionID : "",
        lastSearchData.destination_countryCode ? lastSearchData.destination_countryCode : "",
        lastSearchData.destination_latLong ? lastSearchData.destination_latLong : "",
        lastSearchData.destination_ttla ? lastSearchData.destination_ttla : ""
      );

      this.setFlightClass(
        lastSearchData.flight_class_code ? lastSearchData.flight_class_code : this.flightClassCode
      );

      this.isAddACarChecked = lastSearchData.add_car_check
        ? JSON.parse(lastSearchData.add_car_check)
        : this.isAddACarChecked;
      this.isAddAHotelChecked = lastSearchData.add_hotel_check
        ? JSON.parse(lastSearchData.add_hotel_check)
        : this.isAddAHotelChecked;
      if (this.isAddAHotelChecked) {
        const hotelTravelers = travelers.hotel;
        this.updateDateSelection(startDateToUse, endDateToUse);
        this.updateHotelDateSelection(hotelStartDateToUse, hotelEndDateToUse);
        this.updateHotelTravelersSelection(hotelTravelers);
      } else {
        const nonHotelTravelers = travelers.nonHotel;
        this.updateDateSelection(startDateToUse, endDateToUse);
        this.updateNonHotelTravelersSelection(nonHotelTravelers);
      }

      if (lastSearchData.list_of_additional_legs) {
        this.listOfAdditionalLegs = JSON.parse(lastSearchData.list_of_additional_legs).map(
          (leg: FlightLegState) => {
            return {
              ...leg,
              date: {
                start: new Date(leg.date.start),
                end: new Date(leg.date.end)
              }
            };
          }
        );
      } else {
        this.listOfAdditionalLegs = new Array<FlightLegState>(
          this.config.multiCityFlight.maxNumberOfAdditionalLegs
        );
      }
    }
  };

  public submit = (event: React.FormEvent) => {
    const isValidForm = this.validateForm();
    if (!isValidForm) {
      event.preventDefault();
    } else if (this.canTrack) {
      this.setLastSearchCookie();
    }
  };

  @action
  public updateHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateHotelTravelersSelection(travelers);
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public updateNonHotelTravelersSelection = (travelers: Travelers) => {
    this.globalState.updateNonHotelTravelersSelection(travelers);
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public toggleAddAHotelCheckbox = () => {
    this.isAddAHotelChecked = !this.isAddAHotelChecked;
    this.hotelTravelersInvalidKey = "";
    this.nonHotelTravelersInvalidKey = "";
  };

  @action
  public toggleAddCarCheckbox = () => {
    this.isAddACarChecked = !this.isAddACarChecked;
  };

  constructor(
    config: FlightConfig,
    globalState: GlobalWizardState,
    lastSearchData?: LastSearchData
  ) {
    this.config = config;
    this.globalState = globalState;
    this.listOfAdditionalLegs = [
      {
        location: locationState(),
        date: dateState()
      }
    ];
    this.multiCityFlightLegURL = new Array<string>(
      this.config.multiCityFlight.maxNumberOfAdditionalLegs
    );
    this.hotelDate = dateState();
    if (lastSearchData && this.canTrack) {
      this.lastSearchData = lastSearchData.flight;
      this.updateStateFromCookie();
    }
  }
}
